"use client";

import { useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import CarouselControls from "./carousel-controls";
import normalizeAssetUrl from "../../../lib/assetUrl";

type ImageCarouselProps = {
	images: string[];
	width?: number;
	height?: number;
	className?: string;
};

export default function ImageCarousel({
	images,
	className = "",
}: ImageCarouselProps) {
	const [carouselIndex, setCarouselIndex] = useState(0);
	const normalized = useMemo(() => {
		// Fallback sicuro: evita crash se normalizeAssetUrl non è una funzione
		if (typeof normalizeAssetUrl !== "function") return images;
		return images.map((src) => normalizeAssetUrl(src));
	}, [images]);
	
	const viewportRef = useRef<HTMLDivElement | null>(null);
	const [viewportW, setViewportW] = useState(0);
	const [imageLoaded, setImageLoaded] = useState<boolean[]>(() =>
		Array(normalized.length).fill(false),
	);
	const [dragX, setDragX] = useState(0);
	const [isDragging, setIsDragging] = useState(false);

	const touch = useRef({
		startX: 0,
		startY: 0,
		dx: 0,
		dy: 0,
		swiping: false,
		dragging: false,
	});

	useEffect(() => {
		if (!viewportRef.current) return;
		const ro = new ResizeObserver((entries) => {
			setViewportW(Math.round(entries[0].contentRect.width));
		});
		ro.observe(viewportRef.current);
		return () => ro.disconnect();
	}, []);

	const markLoaded = (index: number) => {
		setImageLoaded((prev) => {
			if (prev[index]) return prev;
			const next = prev.slice();
			next[index] = true;
			return next;
		});
	};

	const bindImg = (el: HTMLImageElement | null, index: number) => {
		if (el && el.complete && el.naturalWidth > 0) markLoaded(index);
	};

	const prev = () => {
		setCarouselIndex((p) => Math.max(p - 1, 0));
	};

	const next = () => {
		setCarouselIndex((p) => Math.min(p + 1, normalized.length - 1));
	};

	const onDotClick = (index: number) => setCarouselIndex(index);

	const SWIPE_THRESHOLD = 60;
	const LOCK_AXIS_THRESHOLD = 10;
	const MAX_DRAG = Math.max(40, Math.floor(viewportW * 0.35));

	const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
		const t = e.touches[0];
		touch.current = {
			startX: t.clientX,
			startY: t.clientY,
			dx: 0,
			dy: 0,
			swiping: false,
			dragging: false,
		};
		setDragX(0);
	};

	const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
		const t = e.touches[0];
		const dx = t.clientX - touch.current.startX;
		const dy = t.clientY - touch.current.startY;
		touch.current.dx = dx;
		touch.current.dy = dy;

		if (!touch.current.swiping) {
			if (Math.abs(dx) > LOCK_AXIS_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
				touch.current.swiping = true;
			}
		}

		if (touch.current.swiping) {
			e.preventDefault();
			touch.current.dragging = true;
			setIsDragging(true);

			let offset = dx;
			const atFirst = carouselIndex === 0 && dx > 0;
			const atLast = carouselIndex === normalized.length - 1 && dx < 0;
			if (atFirst || atLast) {
				offset = Math.sign(dx) * Math.min(Math.abs(dx), MAX_DRAG);
			}

			setDragX(offset);
		}
	};

	const onTouchEnd = () => {
		if (!touch.current.dragging) return;

		const { dx } = touch.current;
		let nextIndex = carouselIndex;

		if (dx <= -SWIPE_THRESHOLD && carouselIndex < normalized.length - 1) {
			nextIndex = carouselIndex + 1;
		} else if (dx >= SWIPE_THRESHOLD && carouselIndex > 0) {
			nextIndex = carouselIndex - 1;
		}

		setCarouselIndex(nextIndex);
		setDragX(0);
		setIsDragging(false);
		touch.current.dragging = false;
		touch.current.swiping = false;
	};

	const onTouchCancel = () => {
		setDragX(0);
		setIsDragging(false);
		touch.current.dragging = false;
		touch.current.swiping = false;
	};

	if (!normalized.length) {
		return null;
	}

	// FIX CRITICO: translateX in pixel, non in percentuale
	const translateXPx = -(carouselIndex * viewportW) + dragX;

	return (
		<div className={`mt-4 mx-auto max-w-5xl relative ${className}`}>
			{/* Viewport del carosello */}
			<div
				ref={viewportRef}
				className="relative w-full overflow-hidden rounded-lg shadow-lg bg-white"
				style={{ 
					height: "70vh",
					maxHeight: "800px",
					minHeight: "400px"
				}}
			>
				<div
					className="flex h-full touch-none select-none cursor-grab active:cursor-grabbing"
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					onTouchEnd={onTouchEnd}
					onTouchCancel={onTouchCancel}
					style={{
						transform: `translateX(${translateXPx}px)`,
						transition: isDragging ? "none" : "transform 400ms cubic-bezier(0.4, 0, 0.2, 1)",
					}}
				>
					{normalized.map((src, i) => (
						<div
							key={`${src}-${i}`}
							className="shrink-0 flex justify-center items-center h-full bg-white"
							style={{ width: viewportW ? `${viewportW}px` : "100%" }}
						>
							{src ? (
								<img
									ref={(el) => bindImg(el, i)}
									src={src}
									alt={`Slide ${i + 1}`}
									className="max-w-full max-h-full object-contain"
									decoding="async"
									loading="eager"
									draggable={false}
									onLoad={() => markLoaded(i)}
									onError={() => markLoaded(i)}
								/>
							) : (
								<div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
									<p>Nessuna immagine</p>
								</div>
							)}
						</div>
					))}
				</div>

				{!imageLoaded[carouselIndex] && normalized[carouselIndex] && (
					<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
						<div className="w-10 h-10 border-4 border-[#10b0ff] border-t-transparent rounded-full animate-spin" />
					</div>
				)}
			</div>

			{/* Controlli del carosello */}
			<CarouselControls
				total={normalized.length}
				activeIndex={carouselIndex}
				onPrev={prev}
				onNext={next}
				disablePrev={carouselIndex === 0}
				disableNext={carouselIndex === normalized.length - 1}
				onDotClick={onDotClick}
			/>

			{/* Debug info
			<div className="mt-2 text-xs text-gray-500 text-center">
				Index: {carouselIndex + 1}/{normalized.length} | 
				ViewportW: {viewportW}px | 
				DragX: {Math.round(dragX)}px |
				TranslateX: {Math.round(translateXPx)}px
			</div> */}
		</div>
	);
}